import { HubMapConfig, HubMapId } from '../types/hub.types';
import { DirectionEnum } from '../types/enums';

export type { HubMapId };

/**
 * Hub maps. The hub is split into several **maps** (the bedroom by day, by
 * night, and any future lore locations). Each map is a self-contained config:
 * its own background art (awake + dream layers), `collision.png` mask (red pixels R > 128 =
 * non-walkable), spawn points, and interactive NPC/station placements — so a
 * map switch can change skin, collisions AND element placement. The current
 * map is selected via `useHubStore.currentHubMapId` and switched with
 * `hubService.switchMap()` (fade in/out). Night is "another hub".
 *
 * Coordinates are in **logical map pixels** (= native PNG pixels, Y grows
 * downward). Zoom and Y negation for PIXI are applied at render time.
 *
 * NPCs/stations: each entry references an `npcId` from `NPC_REGISTRY`. Stations
 * are invisible **points of interest** by default (no `spriteName`/`collision`):
 * the station art is painted into the backgrounds, the player walks through them,
 * and interaction uses the global `interactionRange`. An entry may still set a
 * `spriteName` (+ `collision`) to render an overlay sprite at that point.
 */
const DAY_MAP: HubMapConfig = {
  awakeBackgroundUrl: '/sprites/hub/background-awake.png',
  dreamBackgroundUrl: '/sprites/hub/background-dream.png',
  dreamBubble: {
    centerX: 71,
    centerY: 66,
    maskUrl: '/sprites/hub/dream-bubble.png',
    growDurationMs: 1100,
  },
  collisionMaskUrl: '/sprites/hub/collision.png',
  width: 218,
  height: 144,
  spawnPoints: {
    default: { x: 103, y: 70, facing: DirectionEnum['BOTTOM-RIGHT'] },
  },
  bed: { x: 94, y: 50 },
  npcs: [
    { npcId: 'cartographer', x: 96, y: 52, promptOffset: 9 },
    { npcId: 'bestiary', x: 198, y: 61, promptOffset: 23 },
    { npcId: 'chest-keeper', x: 61, y: 68, promptOffset: 15 },
    { npcId: 'commode', x: 134, y: 57, promptOffset: 18 },
    { npcId: 'shop', x: 35, y: 68, promptOffset: 15 },
    { npcId: 'loadout', x: 88, y: 70, promptOffset: 9 },
    { npcId: 'multiplayer-station', x: 160, y: 70, promptOffset: 12 },
  ],
};

/**
 * Night variant — placeholder: reuses the day layout/collision/stations with a
 * night skin. Diverge freely (its own collision mask, repositioned or
 * different stations) when the night-hub art/lore lands. Dream overlay is opted
 * out until night dream art exists — otherwise it would reveal day-colored dream
 * pixels under a night awake layer.
 */
const NIGHT_MAP: HubMapConfig = {
  ...DAY_MAP,
  awakeBackgroundUrl: '/sprites/hub/background-awake-night.png',
  dreamBackgroundUrl: undefined,
  dreamBubble: undefined,
};

/**
 * Pre-dungeon-1 hub: 144×144 = the full hub minus the left 74 px (the zone
 * revealed by the cloud after D1). Own coordinate system (full_x − 74). Single
 * background, no dream layer. Only the cartographer is reachable; the other
 * stations live in the left/hidden zone or are unlock-gated.
 */
const INITIAL_MAP: HubMapConfig = {
  awakeBackgroundUrl: '/sprites/hub/initial-hub.png',
  collisionMaskUrl: '/sprites/hub/initial-collision.png',
  width: 144,
  height: 144,
  spawnPoints: {
    default: { x: 29, y: 70, facing: DirectionEnum['BOTTOM-RIGHT'] },
  },
  bed: { x: 20, y: 50 },
  npcs: [{ npcId: 'cartographer', x: 22, y: 52, promptOffset: 9 }],
};

export const DEFAULT_HUB_MAP_ID: HubMapId = 'day';

export const HUB_MAPS: Record<HubMapId, HubMapConfig> = {
  day: DAY_MAP,
  night: NIGHT_MAP,
  initial: INITIAL_MAP,
};
